import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import type { TDocumentDefinitions, TableCell } from "pdfmake/interfaces";

import type { Consultation } from "@/types/api/consultation";
import { getConsultationStepLabel } from "@/types/api/consultation";
import type { Patient } from "@/types/api/patient";
import {
  formatFullDate,
  getAge,
  getConsultationLabel,
  getDiabetLabel,
  getDiseaseLabel,
  getGravityStateLabel,
  getName,
  getSmokingPatientLabel,
  getToothBrushHairType,
  getToothBrushType,
} from "@/utils/patient";
import { getConsultationFields } from "@/utils/treatment";

(<any>pdfMake).addVirtualFileSystem(pdfFonts);

export const exportPatientPDF = (
  patient: Patient,
  consultations: Array<Consultation>
) => {
  const fullName = getName(patient);
  const age = getAge(patient.birthDate);
  const consultationFields = consultations.map((consultation) =>
    getConsultationFields(consultation)
  );

  const docDefinition: TDocumentDefinitions = {
    content: [
      { text: "Fiche Patient", style: "header" },
      {
        canvas: [{ type: "line", x1: 0, y1: 5, x2: 515, y2: 5, lineWidth: 1 }],
      },
      {
        style: "section",
        table: {
          widths: ["auto", "*"],
          body: [
            [{ text: "Nom", bold: true }, fullName],
            [
              { text: "Genre", bold: true },
              patient.gender === "MALE"
                ? "Homme"
                : patient.gender === "FEMALE"
                  ? "Femme"
                  : "Autre",
            ],
            [{ text: "Âge", bold: true }, `${age} ans`],
            [{ text: "Email", bold: true }, patient.email],
            [
              { text: "Téléphone", bold: true },
              patient.mobilePhone ?? "Non renseigné",
            ],
            [
              { text: "Adresse", bold: true },
              `${patient.address ?? ""} ${patient.postalCode ?? ""} ${patient.city ?? ""}`,
            ],
          ],
        },
        layout: "lightHorizontalLines",
      },

      { text: "Profil patient", style: "subheader" },
      {
        style: "section",
        table: {
          widths: ["auto", "*", "*", "*"],
          body: [
            [
              { text: "Motif consultation", bold: true },
              getConsultationLabel(patient.consultationMotive) ??
                "Non renseigné",
              {},
              {},
            ],
            [
              { text: "Maladie", bold: true },
              getDiseaseLabel(patient.disease) ?? "Non renseigné",
              {},
              {},
            ],
            [
              { text: "Brosse à dents", bold: true },
              patient.toothBrush
                ? getToothBrushType(patient.toothBrush)
                : "Non renseigné",
              {},
              {},
            ],
            [
              { text: "Souplesse poils", bold: true },
              patient.toothBrushHair
                ? getToothBrushHairType(patient.toothBrushHair)
                : "Non renseigné",
              {},
              {},
            ],
            [
              { text: "Tabagisme", bold: true },
              getSmokingPatientLabel(patient.smoking) ?? "Non renseigné",
              getGravityStateLabel(patient.smokingState),
              patient.smoking === "PREVIOUS"
                ? `Arrêté le ${patient.smokingEnd ? formatFullDate(new Date(patient.smokingEnd)) : "Non renseigné"}`
                : `${patient.dailySmokes?.toString() ?? "Non renseigné"}`,
            ],
            [
              { text: "Diabète", bold: true },
              getDiabetLabel(patient.diabetic) ?? "Non renseigné",
              getGravityStateLabel(patient.diabetState),
              {},
            ],
            [
              { text: "Stress", bold: true },
              `${patient.stress?.toString() ?? "Non renseigné"}/10`,
              getGravityStateLabel(patient.stressState),
              {},
            ],
          ],
        },
        layout: "lightHorizontalLines",
      },

      { text: "Prise en charge", style: "subheader" },
      ...consultations
        .sort(
          (a, b) =>
            new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
        )
        .slice(0, 5)
        .map((consult, index) => {
          const rows: Array<Array<TableCell>> = [];

          // Add title row
          rows.push([
            {
              text: `${getConsultationStepLabel(consult.consultationStep)} (${formatFullDate(
                new Date(consult.startDate)
              )} - ${formatFullDate(new Date(consult.endDate))})`,
              colSpan: 2,
              bold: true,
              fillColor: "#eeeeee",
              margin: [0, 5, 0, 5],
            },
            {},
          ]);

          // Optional comment row
          if (consult.comment) {
            rows.push([
              { text: "Commentaire", bold: true },
              { text: consult.comment, italics: true },
            ]);
          }

          // Add custom fields
          consultationFields[index].forEach((extraFields) => {
            if (!extraFields.hidden) {
              extraFields.fields.forEach((field) => {
                if (
                  field.value !== "" &&
                  field.value !== null &&
                  field.value !== undefined
                ) {
                  rows.push([
                    { text: field.label, bold: true },
                    { text: field.value ?? "Non renseigné", italics: true },
                  ]);
                }
              });
            }
          });

          return {
            style: "consultBlock",
            table: {
              widths: ["auto", "*"],
              body: rows,
            },
            layout: "lightHorizontalLines",
          };
        }),
    ],
    styles: {
      header: {
        fontSize: 22,
        bold: true,
        margin: [0, 0, 0, 10],
      },
      subheader: {
        fontSize: 14,
        bold: true,
        margin: [0, 15, 0, 5],
      },
      section: {
        margin: [0, 0, 0, 10],
      },
      consultBlock: {
        margin: [0, 2, 0, 2],
      },
    },
    defaultStyle: {
      fontSize: 10,
    },
  };

  return docDefinition;
};
